import { Box, Flex } from '@/components/atoms/Grid'
import { ArrowIconRightDefault } from '@/components/atoms/icons/ArrowIconRightDefault'
import Link from '@/components/atoms/link'
import { Body, H2, Small3 } from '@/components/atoms/typography'
import { Blog } from '@/network/graphql.g'
import { validFor } from '@/theme/validFor'
import useTranslation from 'next-translate/useTranslation'
import { FC } from 'react'
import styled from 'styled-components'
import { DateTime } from 'luxon'
import ResponsiveImage from '@/components/atoms/responsiveImage'
import useDevice from 'hooks/useDevice'

type MainArticleProps = {
  mainArticle?: Blog
}

const BlogMainArticle: FC<MainArticleProps> = ({ mainArticle }) => {
  const { t } = useTranslation('common')
  const { isMobile } = useDevice()
  if (!mainArticle) {
    return null
  }
  const { title, desktopImage, mobileImage, url } = mainArticle
  const createdAt = mainArticle.createdAt
    ? DateTime.fromISO(mainArticle.createdAt).toFormat('dd.MM.yyyy')
    : ''

  const alternativeImage = (e) => {
    e.target.src = 'https://i.factcool.com/summer/landing-page-summer_11.jpg'
  }

  return (
    <MainArticleWrapper>
      <ImageWrapper>
        <ResponsiveImage
          imageUrl={isMobile ? mobileImage : desktopImage}
          alt="Fashion Blog"
          width={1000}
          height={450}
          objectFit="cover"
          onError={alternativeImage}
        />
        {createdAt && (
          <DateBox>
            <Body>{createdAt}</Body>
          </DateBox>
        )}
      </ImageWrapper>
      <StyledFlex alignItems="center" textAlign="center" flexDirection="column">
        <MainTitle mt="40px" mb="5px">
          {title}
        </MainTitle>
        <Box>
          <StyledLink href={url}>
            <Small3>{t('Blog.viewArticleButton')}</Small3>
            <ArrowIconRightDefault />
          </StyledLink>
        </Box>
      </StyledFlex>
    </MainArticleWrapper>
  )
}

export default BlogMainArticle

const MainArticleWrapper = styled(Flex)`
  flex-direction: column;
  align-items: center;
  width: 100%;
`

const ImageWrapper = styled(Box)`
  position: relative;
  width: 100%;
`

const DateBox = styled(Box)`
  position: absolute;
  box-sizing: border-box;
  width: 120px;
  padding: 4px 6px;
  text-align: center;
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.onBackground};
  left: 50%;
  transform: translate(-50%, -50%);
  & > p {
    margin: 0;
    line-height: 1;
  }
`

const StyledFlex = styled(Flex)`
  padding: 0 110px 24px;
  ${validFor.mobile` 
    margin-inline: 28px;
    padding-inline: 0;
	`};
`

const MainTitle = styled(H2)`
  font-size: 28px;
  ${validFor.mobile` 
    font-size: 20px;
    line-height: 1.4;
    margin-top: 28px;
	`};
`

const StyledLink = styled(Link)`
  padding: 2px 20px;
  width: 150px;
  color: ${(props) => props.theme.colors.accent};
  display: flex;
  justify-content: center;
  gap: 6px;
`
